import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, Check, Loader2, PenTool, RotateCcw, Plus, Trash2, Upload, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Textarea } from '../components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ClientIntakePage = () => {
    const [currentSlide, setCurrentSlide] = useState(0);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);
    const [direction, setDirection] = useState(0);
    const [logoPreview, setLogoPreview] = useState(null);
    const [logoFile, setLogoFile] = useState(null);
    const logoInputRef = useRef(null);
    
    // Form data
    const [formData, setFormData] = useState({
        // Slide 3 - Contact
        gender: '',
        first_name: '',
        last_name: '',
        position: '',
        email: '',
        phone: '',
        
        // Slide 4 - Project participants
        participants: [{ name: '', email: '', phone: '', position: '' }],
        
        // Slide 5 - Company
        company_name: '',
        company_address: '',
        billing_email: '',
        billing_address: '',
        
        // Slide 6
        website: '',
        
        // Slide 7
        business_type: '',
        
        // Slide 8
        products_services: '',
        
        // Slide 9
        keywords: '',
        
        // Slide 10 - Logo (handled separately)
        
        // Slide 11
        tagline: '',
        
        // Slide 12
        operating_regions: '',
        
        // Slide 13
        language: '',
        
        // Slide 14
        parking: '',
        
        // Slide 15
        special_offers: '',
        
        // Slide 17 - Social channels
        social_channels: {
            facebook: false,
            instagram: false,
            tiktok: false,
            linkedin: false,
            google_business: false
        },
        
        // Slide 18 - Logins (sensitive!)
        other_logins: [{ url: '', username: '', password: '' }],
        
        // Slide 19
        previous_info: ''
    });

    const totalSlides = 20;

    const updateField = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const updateParticipant = (index, field, value) => {
        setFormData(prev => {
            const updated = [...prev.participants];
            updated[index] = { ...updated[index], [field]: value };
            return { ...prev, participants: updated };
        });
    };

    const addParticipant = () => {
        setFormData(prev => ({
            ...prev,
            participants: [...prev.participants, { name: '', email: '', phone: '', position: '' }]
        }));
    };

    const removeParticipant = (index) => {
        if (formData.participants.length > 1) {
            setFormData(prev => ({
                ...prev,
                participants: prev.participants.filter((_, i) => i !== index)
            }));
        }
    };

    const updateLogin = (index, field, value) => {
        setFormData(prev => {
            const updated = [...prev.other_logins];
            updated[index] = { ...updated[index], [field]: value };
            return { ...prev, other_logins: updated };
        });
    };

    const addLogin = () => {
        setFormData(prev => ({
            ...prev,
            other_logins: [...prev.other_logins, { url: '', username: '', password: '' }]
        }));
    };

    const removeLogin = (index) => {
        if (formData.other_logins.length > 1) {
            setFormData(prev => ({
                ...prev,
                other_logins: prev.other_logins.filter((_, i) => i !== index)
            }));
        }
    };

    const updateSocialChannel = (channel, value) => {
        setFormData(prev => ({
            ...prev,
            social_channels: { ...prev.social_channels, [channel]: value }
        }));
    };

    const handleLogoChange = (e) => {
        const file = e.target.files?.[0];
        if (file) {
            setLogoFile(file);
            const reader = new FileReader();
            reader.onload = (e) => setLogoPreview(e.target.result);
            reader.readAsDataURL(file);
        }
    };

    const canProceed = () => {
        switch (currentSlide) {
            case 0: return true; // Welcome
            case 1: return true; // Info
            case 2: return formData.gender && formData.first_name && formData.last_name && formData.email; // Contact
            case 3: return true; // Participants (optional)
            case 4: return formData.company_name; // Company
            case 5: return true; // Website (optional)
            case 6: return true; // Business type (optional)
            case 7: return true; // Products (optional)
            case 8: return true; // Keywords (optional)
            case 9: return true; // Logo (optional)
            case 10: return true; // Tagline (optional)
            case 11: return true; // Regions (optional)
            case 12: return true; // Language (optional)
            case 13: return true; // Parking (optional)
            case 14: return true; // Offers (optional)
            case 15: return true; // Account info
            case 16: return true; // Social channels
            case 17: return true; // Logins (optional)
            case 18: return true; // Previous info (optional)
            case 19: return true; // Summary
            default: return true;
        }
    };

    const handleSubmit = async () => {
        setIsSubmitting(true);

        try {
            // Upload logo first if exists
            let logoUrl = null;
            if (logoFile) {
                const logoFormData = new FormData();
                logoFormData.append('file', logoFile);
                try {
                    const logoRes = await axios.post(`${API_URL}/api/upload/image`, logoFormData);
                    logoUrl = logoRes.data.url;
                } catch (err) {
                    console.error('Logo upload failed:', err);
                }
            }

            const submitData = {
                ...formData,
                logo_url: logoUrl,
                submitted_at: new Date().toISOString()
            };

            const res = await axios.post(`${API_URL}/api/client-intake/submit`, submitData);
            
            if (res.data.success) {
                setShowSuccess(true);
            }
        } catch (err) {
            console.error('Submit error:', err);
            toast.error(err.response?.data?.detail || 'Fehler beim Senden');
        } finally {
            setIsSubmitting(false);
        }
    };

    const slideVariants = {
        enter: (direction) => ({ x: direction > 0 ? 300 : -300, opacity: 0 }),
        center: { x: 0, opacity: 1 },
        exit: (direction) => ({ x: direction < 0 ? 300 : -300, opacity: 0 })
    };

    const goNext = () => {
        if (canProceed() && currentSlide < totalSlides - 1) {
            setDirection(1);
            setCurrentSlide(currentSlide + 1);
        }
    };

    const goPrev = () => {
        if (currentSlide > 0) {
            setDirection(-1);
            setCurrentSlide(currentSlide - 1);
        }
    };

    // Success Screen
    if (showSuccess) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex flex-col">
                <div className="flex-1 flex items-center justify-center p-4">
                    <motion.div
                        initial={{ scale: 0.8, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        className="bg-white/10 backdrop-blur-xl rounded-3xl p-8 max-w-md w-full text-center border border-white/20"
                    >
                        <img src="/tgosm-logo.png" alt="TGOSM" className="w-16 h-16 mx-auto mb-4 object-contain" />
                        
                        <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ delay: 0.2, type: 'spring' }}
                            className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6"
                        >
                            <Check className="w-10 h-10 text-white" />
                        </motion.div>
                        
                        <h1 className="text-2xl font-bold text-white mb-4">Vielen Dank!</h1>
                        
                        <p className="text-white/70 mb-6">
                            Unser Team wird Ihre Antworten sorgfältig prüfen. Wir werden uns dann mit Ihnen in Verbindung setzen, um die nächsten Schritte zu besprechen und alle offenen Fragen zu klären.
                        </p>
                        
                        <div className="p-4 bg-white/5 rounded-xl text-sm text-white/60">
                            <p><strong>Firma:</strong> {formData.company_name}</p>
                            <p><strong>Ansprechpartner:</strong> {formData.first_name} {formData.last_name}</p>
                        </div>
                    </motion.div>
                </div>
                
                <footer className="py-6 text-center text-white/40 text-sm">
                    <p>© {new Date().getFullYear()} The Guardians of Social Media GmbH</p>
                </footer>
            </div>
        );
    }

    const renderSlide = () => {
        switch (currentSlide) {
            // Slide 0: Welcome
            case 0:
                return (
                    <div className="space-y-6 text-center py-4">
                        <h2 className="text-2xl font-bold text-white">Willkommen bei "The Guardians"</h2>
                        <p className="text-white/70 leading-relaxed">
                            Bevor wir mit unserer Zusammenarbeit beginnen, möchten wir Sie besser kennenlernen. 
                            Das hilft uns dabei, unsere Dienstleistungen optimal auf Ihre Bedürfnisse abzustimmen 
                            und sicherzustellen, dass wir gemeinsam erfolgreich sind.
                        </p>
                    </div>
                );
            
            // Slide 1: Info
            case 1:
                return (
                    <div className="space-y-6 py-2">
                        <h2 className="text-xl font-bold text-white">Warum ein Client Intake-Formular?</h2>
                        <div className="space-y-4 text-white/70 text-sm">
                            <div className="p-3 bg-white/5 rounded-lg">
                                <strong className="text-white">1. Persönliche Abstimmung:</strong>
                                <p className="mt-1">Durch das Ausfüllen dieses Formulars geben Sie uns wertvolle Informationen über Ihr Unternehmen, Ihre Ziele und Ihre Erwartungen.</p>
                            </div>
                            <div className="p-3 bg-white/5 rounded-lg">
                                <strong className="text-white">2. Effizienz:</strong>
                                <p className="mt-1">Mit den von Ihnen bereitgestellten Informationen können wir den Prozess beschleunigen.</p>
                            </div>
                            <div className="p-3 bg-white/5 rounded-lg">
                                <strong className="text-white">3. Klare Kommunikation:</strong>
                                <p className="mt-1">Dieses Formular stellt sicher, dass wir alle notwendigen Informationen erhalten.</p>
                            </div>
                        </div>
                        <div className="p-4 bg-primary/20 rounded-xl">
                            <h3 className="font-semibold text-white mb-2">Wie geht es weiter?</h3>
                            <p className="text-white/70 text-sm">
                                Nachdem Sie das Formular ausgefüllt haben, wird unser Team Ihre Antworten prüfen und sich mit Ihnen in Verbindung setzen.
                            </p>
                        </div>
                    </div>
                );
            
            // Slide 2: Contact Info
            case 2:
                return (
                    <div className="space-y-4">
                        <Label className="text-white text-lg">Kontaktinformationen *</Label>
                        
                        <div>
                            <Label className="text-white/70 text-sm">Anrede</Label>
                            <RadioGroup value={formData.gender} onValueChange={(v) => updateField('gender', v)} className="flex gap-4 mt-2">
                                <div className="flex items-center gap-2">
                                    <RadioGroupItem value="male" id="male" />
                                    <Label htmlFor="male" className="text-white cursor-pointer">Männlich</Label>
                                </div>
                                <div className="flex items-center gap-2">
                                    <RadioGroupItem value="female" id="female" />
                                    <Label htmlFor="female" className="text-white cursor-pointer">Weiblich</Label>
                                </div>
                            </RadioGroup>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label className="text-white/70 text-sm">Vorname *</Label>
                                <Input
                                    value={formData.first_name}
                                    onChange={(e) => updateField('first_name', e.target.value)}
                                    className="bg-white/10 border-white/20 text-white mt-1"
                                    placeholder="Max"
                                />
                            </div>
                            <div>
                                <Label className="text-white/70 text-sm">Nachname *</Label>
                                <Input
                                    value={formData.last_name}
                                    onChange={(e) => updateField('last_name', e.target.value)}
                                    className="bg-white/10 border-white/20 text-white mt-1"
                                    placeholder="Mustermann"
                                />
                            </div>
                        </div>
                        
                        <div>
                            <Label className="text-white/70 text-sm">Position / Berufsbezeichnung</Label>
                            <Input
                                value={formData.position}
                                onChange={(e) => updateField('position', e.target.value)}
                                className="bg-white/10 border-white/20 text-white mt-1"
                                placeholder="Geschäftsführer"
                            />
                        </div>
                        
                        <div>
                            <Label className="text-white/70 text-sm">E-Mail *</Label>
                            <Input
                                type="email"
                                value={formData.email}
                                onChange={(e) => updateField('email', e.target.value)}
                                className="bg-white/10 border-white/20 text-white mt-1"
                                placeholder="max@firma.at"
                            />
                        </div>
                        
                        <div>
                            <Label className="text-white/70 text-sm">Telefonnummer</Label>
                            <Input
                                type="tel"
                                value={formData.phone}
                                onChange={(e) => updateField('phone', e.target.value)}
                                className="bg-white/10 border-white/20 text-white mt-1"
                                placeholder="+43 1 234 5678"
                            />
                        </div>
                    </div>
                );
            
            // Slide 3: Project Participants
            case 3:
                return (
                    <div className="space-y-4">
                        <Label className="text-white text-lg">Kontakt der Projektbeteiligten</Label>
                        <p className="text-white/60 text-sm">Fügen Sie weitere Ansprechpartner hinzu</p>
                        
                        <div className="space-y-3 max-h-[280px] overflow-y-auto">
                            {formData.participants.map((p, i) => (
                                <div key={i} className="p-3 bg-white/5 rounded-xl space-y-2">
                                    <div className="flex justify-between items-center">
                                        <span className="text-white/60 text-xs">Person {i + 1}</span>
                                        {formData.participants.length > 1 && (
                                            <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => removeParticipant(i)}>
                                                <Trash2 className="w-3 h-3 text-red-400" />
                                            </Button>
                                        )}
                                    </div>
                                    <div className="grid grid-cols-2 gap-2">
                                        <Input
                                            value={p.name}
                                            onChange={(e) => updateParticipant(i, 'name', e.target.value)}
                                            className="bg-white/10 border-white/20 text-white text-sm h-9"
                                            placeholder="Name"
                                        />
                                        <Input
                                            value={p.position}
                                            onChange={(e) => updateParticipant(i, 'position', e.target.value)}
                                            className="bg-white/10 border-white/20 text-white text-sm h-9"
                                            placeholder="Position"
                                        />
                                    </div>
                                    <div className="grid grid-cols-2 gap-2">
                                        <Input
                                            type="email"
                                            value={p.email}
                                            onChange={(e) => updateParticipant(i, 'email', e.target.value)}
                                            className="bg-white/10 border-white/20 text-white text-sm h-9"
                                            placeholder="E-Mail"
                                        />
                                        <Input
                                            type="tel"
                                            value={p.phone}
                                            onChange={(e) => updateParticipant(i, 'phone', e.target.value)}
                                            className="bg-white/10 border-white/20 text-white text-sm h-9"
                                            placeholder="Telefon"
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>
                        
                        <Button variant="outline" onClick={addParticipant} className="w-full border-dashed border-white/30 text-white hover:bg-white/10">
                            <Plus className="w-4 h-4 mr-2" /> Person hinzufügen
                        </Button>
                    </div>
                );
            
            // Slide 4: Company Info
            case 4:
                return (
                    <div className="space-y-4">
                        <Label className="text-white text-lg">Firmeninformationen *</Label>
                        
                        <div>
                            <Label className="text-white/70 text-sm">Eingetragener Firmenname *</Label>
                            <Input
                                value={formData.company_name}
                                onChange={(e) => updateField('company_name', e.target.value)}
                                className="bg-white/10 border-white/20 text-white mt-1"
                                placeholder="Muster GmbH"
                            />
                        </div>
                        
                        <div>
                            <Label className="text-white/70 text-sm">Firmenadresse</Label>
                            <Textarea
                                value={formData.company_address}
                                onChange={(e) => updateField('company_address', e.target.value)}
                                className="bg-white/10 border-white/20 text-white mt-1"
                                placeholder="Musterstraße 1, 1010 Wien"
                                rows={2}
                            />
                        </div>
                        
                        <div>
                            <Label className="text-white/70 text-sm">Rechnungs-E-Mail Adresse</Label>
                            <Input
                                type="email"
                                value={formData.billing_email}
                                onChange={(e) => updateField('billing_email', e.target.value)}
                                className="bg-white/10 border-white/20 text-white mt-1"
                                placeholder="rechnung@firma.at"
                            />
                            <p className="text-white/50 text-xs mt-1">Wohin sollen Rechnungen gesendet werden?</p>
                        </div>
                        
                        <div>
                            <Label className="text-white/70 text-sm">Rechnungsadresse (falls abweichend)</Label>
                            <Textarea
                                value={formData.billing_address}
                                onChange={(e) => updateField('billing_address', e.target.value)}
                                className="bg-white/10 border-white/20 text-white mt-1"
                                placeholder="Falls anders als Firmenadresse"
                                rows={2}
                            />
                        </div>
                    </div>
                );
            
            // Slide 5: Website
            case 5:
                return (
                    <div className="space-y-4">
                        <Label className="text-white text-lg">Website</Label>
                        <Input
                            value={formData.website}
                            onChange={(e) => updateField('website', e.target.value)}
                            className="bg-white/10 border-white/20 text-white"
                            placeholder="https://www.ihre-website.at"
                        />
                        <p className="text-white/50 text-sm">Geben Sie den Link zu Ihrer Website an</p>
                    </div>
                );
            
            // Slide 6: Business Type
            case 6:
                return (
                    <div className="space-y-4">
                        <Label className="text-white text-lg">Geschäftsart / Kategorie</Label>
                        <Input
                            value={formData.business_type}
                            onChange={(e) => updateField('business_type', e.target.value)}
                            className="bg-white/10 border-white/20 text-white"
                            placeholder="z.B. Restaurant, E-Commerce, Dienstleister..."
                        />
                        <p className="text-white/50 text-sm">Wählen Sie bitte Ihre Geschäftsart / Kategorie aus</p>
                    </div>
                );
            
            // Slide 7: Products/Services
            case 7:
                return (
                    <div className="space-y-4">
                        <Label className="text-white text-lg">Produkte / Leistungen / Fachgebiete</Label>
                        <Textarea
                            value={formData.products_services}
                            onChange={(e) => updateField('products_services', e.target.value)}
                            className="bg-white/10 border-white/20 text-white"
                            placeholder="Beschreiben Sie Ihre Hauptprodukte oder Dienstleistungen..."
                            rows={4}
                        />
                        <p className="text-white/50 text-sm">Geben Sie uns eine kleine Übersicht Ihrer Produkte / Leistungen</p>
                    </div>
                );
            
            // Slide 8: Keywords
            case 8:
                return (
                    <div className="space-y-4">
                        <Label className="text-white text-lg">Keywords / Tags</Label>
                        <Textarea
                            value={formData.keywords}
                            onChange={(e) => updateField('keywords', e.target.value)}
                            className="bg-white/10 border-white/20 text-white"
                            placeholder="z.B. nachhaltig, premium, lokal, handgemacht..."
                            rows={3}
                        />
                        <p className="text-white/50 text-sm">Wenn Sie Ihr Business mit Keywords beschreiben würden, welche wären das?</p>
                    </div>
                );
            
            // Slide 9: Logo Upload
            case 9:
                return (
                    <div className="space-y-4">
                        <Label className="text-white text-lg">Aktuelles Logo</Label>
                        
                        <div 
                            onClick={() => logoInputRef.current?.click()}
                            className="border-2 border-dashed border-white/30 rounded-xl p-8 text-center cursor-pointer hover:border-white/50 transition-colors"
                        >
                            {logoPreview ? (
                                <div className="relative">
                                    <img src={logoPreview} alt="Logo Preview" className="max-h-32 mx-auto rounded-lg" />
                                    <Button 
                                        variant="destructive" 
                                        size="icon" 
                                        className="absolute top-0 right-0 h-6 w-6"
                                        onClick={(e) => { e.stopPropagation(); setLogoPreview(null); setLogoFile(null); }}
                                    >
                                        <X className="w-3 h-3" />
                                    </Button>
                                </div>
                            ) : (
                                <>
                                    <Upload className="w-10 h-10 mx-auto text-white/50 mb-3" />
                                    <p className="text-white/70">Klicken zum Hochladen</p>
                                    <p className="text-white/50 text-sm">PNG, JPG bis 5MB</p>
                                </>
                            )}
                        </div>
                        <input
                            ref={logoInputRef}
                            type="file"
                            accept="image/*"
                            onChange={handleLogoChange}
                            className="hidden"
                        />
                    </div>
                );
            
            // Slide 10: Tagline
            case 10:
                return (
                    <div className="space-y-4">
                        <Label className="text-white text-lg">Tag Line / Business Slogan</Label>
                        <Input
                            value={formData.tagline}
                            onChange={(e) => updateField('tagline', e.target.value)}
                            className="bg-white/10 border-white/20 text-white"
                            placeholder="z.B. 'Quality you can taste'"
                        />
                        <p className="text-white/50 text-sm">Geben Sie bitte Ihren Slogan ein</p>
                    </div>
                );
            
            // Slide 11: Operating Regions
            case 11:
                return (
                    <div className="space-y-4">
                        <Label className="text-white text-lg">Tätigkeitsgebiete</Label>
                        <Textarea
                            value={formData.operating_regions}
                            onChange={(e) => updateField('operating_regions', e.target.value)}
                            className="bg-white/10 border-white/20 text-white"
                            placeholder="z.B. Wien, Niederösterreich, ganz Österreich..."
                            rows={3}
                        />
                        <p className="text-white/50 text-sm">In welchen Städten/Gebieten sind Sie tätig?</p>
                    </div>
                );
            
            // Slide 12: Language
            case 12:
                return (
                    <div className="space-y-4">
                        <Label className="text-white text-lg">Welche Sprache nutzen Sie?</Label>
                        <Input
                            value={formData.language}
                            onChange={(e) => updateField('language', e.target.value)}
                            className="bg-white/10 border-white/20 text-white"
                            placeholder="z.B. Deutsch, Englisch..."
                        />
                        <p className="text-white/50 text-sm">In welcher Sprache kommunizieren Sie mit Ihrer Kundschaft auf Social Media?</p>
                    </div>
                );
            
            // Slide 13: Parking
            case 13:
                return (
                    <div className="space-y-4">
                        <Label className="text-white text-lg">Parkmöglichkeiten?</Label>
                        <RadioGroup value={formData.parking} onValueChange={(v) => updateField('parking', v)} className="space-y-3">
                            <div className="flex items-center gap-3 p-3 bg-white/5 rounded-lg">
                                <RadioGroupItem value="yes_paid" id="yes_paid" />
                                <Label htmlFor="yes_paid" className="text-white cursor-pointer flex-1">Ja - kostenpflichtig</Label>
                            </div>
                            <div className="flex items-center gap-3 p-3 bg-white/5 rounded-lg">
                                <RadioGroupItem value="yes_free" id="yes_free" />
                                <Label htmlFor="yes_free" className="text-white cursor-pointer flex-1">Ja - kostenlos</Label>
                            </div>
                            <div className="flex items-center gap-3 p-3 bg-white/5 rounded-lg">
                                <RadioGroupItem value="no" id="no_parking" />
                                <Label htmlFor="no_parking" className="text-white cursor-pointer flex-1">Nein</Label>
                            </div>
                        </RadioGroup>
                    </div>
                );
            
            // Slide 14: Special Offers
            case 14:
                return (
                    <div className="space-y-4">
                        <Label className="text-white text-lg">Gutscheine und Sonderangebote</Label>
                        <Textarea
                            value={formData.special_offers}
                            onChange={(e) => updateField('special_offers', e.target.value)}
                            className="bg-white/10 border-white/20 text-white"
                            placeholder="Beschreiben Sie aktuelle Angebote..."
                            rows={4}
                        />
                        <p className="text-white/50 text-sm">Welche Gutscheine und Sonderangebote bieten Sie derzeit an?</p>
                    </div>
                );
            
            // Slide 15: Account Info
            case 15:
                return (
                    <div className="space-y-6 py-4">
                        <h2 className="text-xl font-bold text-white">Account Informationen</h2>
                        <div className="p-4 bg-white/5 rounded-xl text-white/70 text-sm leading-relaxed">
                            <p>
                                Um eine effektive und zielgerichtete Strategie für Sie zu entwickeln, benötigen wir Zugang zu Ihren verschiedenen Online-Plattformen.
                            </p>
                            <p className="mt-3">
                                Dies ermöglicht es uns, Ihre aktuelle Online-Präsenz zu analysieren und gezielte Empfehlungen vorzunehmen.
                            </p>
                        </div>
                    </div>
                );
            
            // Slide 16: Social Channels
            case 16:
                return (
                    <div className="space-y-4">
                        <Label className="text-white text-lg">Social Media Kanäle</Label>
                        <p className="text-white/60 text-sm">Welche Kanäle dürfen wir für Sie betreuen?</p>
                        
                        <div className="space-y-3">
                            {[
                                { key: 'facebook', label: 'Facebook' },
                                { key: 'instagram', label: 'Instagram' },
                                { key: 'tiktok', label: 'TikTok' },
                                { key: 'linkedin', label: 'LinkedIn' },
                                { key: 'google_business', label: 'Google My Business' }
                            ].map(channel => (
                                <label key={channel.key} className="flex items-center gap-3 p-3 bg-white/5 rounded-lg cursor-pointer hover:bg-white/10">
                                    <Checkbox
                                        checked={formData.social_channels[channel.key]}
                                        onCheckedChange={(v) => updateSocialChannel(channel.key, v)}
                                    />
                                    <span className="text-white">{channel.label}</span>
                                </label>
                            ))}
                        </div>
                    </div>
                );
            
            // Slide 17: Other Logins
            case 17:
                return (
                    <div className="space-y-4">
                        <Label className="text-white text-lg">Weitere Logins</Label>
                        <p className="text-white/60 text-sm">Geben Sie alle anderen Logins an, die Sie für Ihr Unternehmen nutzen</p>
                        <p className="text-yellow-400/80 text-xs">🔒 Diese Daten werden verschlüsselt übertragen</p>
                        
                        <div className="space-y-3 max-h-[220px] overflow-y-auto">
                            {formData.other_logins.map((login, i) => (
                                <div key={i} className="p-3 bg-white/5 rounded-xl space-y-2">
                                    <div className="flex justify-between items-center">
                                        <span className="text-white/60 text-xs">Login {i + 1}</span>
                                        {formData.other_logins.length > 1 && (
                                            <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => removeLogin(i)}>
                                                <Trash2 className="w-3 h-3 text-red-400" />
                                            </Button>
                                        )}
                                    </div>
                                    <Input
                                        value={login.url}
                                        onChange={(e) => updateLogin(i, 'url', e.target.value)}
                                        className="bg-white/10 border-white/20 text-white text-sm h-9"
                                        placeholder="Login URL"
                                    />
                                    <div className="grid grid-cols-2 gap-2">
                                        <Input
                                            value={login.username}
                                            onChange={(e) => updateLogin(i, 'username', e.target.value)}
                                            className="bg-white/10 border-white/20 text-white text-sm h-9"
                                            placeholder="Benutzername"
                                        />
                                        <Input
                                            type="password"
                                            value={login.password}
                                            onChange={(e) => updateLogin(i, 'password', e.target.value)}
                                            className="bg-white/10 border-white/20 text-white text-sm h-9"
                                            placeholder="Passwort"
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>
                        
                        <Button variant="outline" onClick={addLogin} className="w-full border-dashed border-white/30 text-white hover:bg-white/10">
                            <Plus className="w-4 h-4 mr-2" /> Login hinzufügen
                        </Button>
                    </div>
                );
            
            // Slide 18: Previous Info
            case 18:
                return (
                    <div className="space-y-4">
                        <Label className="text-white text-lg">Frühere Firmendaten</Label>
                        <Textarea
                            value={formData.previous_info}
                            onChange={(e) => updateField('previous_info', e.target.value)}
                            className="bg-white/10 border-white/20 text-white"
                            placeholder="z.B. alter Firmenname, frühere Telefonnummer, andere Adresse..."
                            rows={4}
                        />
                        <p className="text-white/50 text-sm">
                            Wenn Sie in der Vergangenheit einen anderen Firmennamen, eine andere Telefonnummer oder eine andere Adresse verwendet haben, geben Sie hier bitte ALLE Varianten an.
                        </p>
                    </div>
                );
            
            // Slide 19: Summary
            case 19:
                return (
                    <div className="space-y-4">
                        <Label className="text-white text-lg">Zusammenfassung</Label>
                        <div className="space-y-2 text-sm max-h-[300px] overflow-y-auto">
                            <div className="p-3 bg-white/5 rounded-lg">
                                <span className="text-white/60">Firma:</span>
                                <span className="text-white ml-2">{formData.company_name || '-'}</span>
                            </div>
                            <div className="p-3 bg-white/5 rounded-lg">
                                <span className="text-white/60">Ansprechpartner:</span>
                                <span className="text-white ml-2">{formData.first_name} {formData.last_name}</span>
                            </div>
                            <div className="p-3 bg-white/5 rounded-lg">
                                <span className="text-white/60">E-Mail:</span>
                                <span className="text-white ml-2">{formData.email || '-'}</span>
                            </div>
                            <div className="p-3 bg-white/5 rounded-lg">
                                <span className="text-white/60">Website:</span>
                                <span className="text-white ml-2">{formData.website || '-'}</span>
                            </div>
                            <div className="p-3 bg-white/5 rounded-lg">
                                <span className="text-white/60">Social Media:</span>
                                <span className="text-white ml-2">
                                    {Object.entries(formData.social_channels).filter(([_, v]) => v).map(([k]) => k).join(', ') || 'Keine ausgewählt'}
                                </span>
                            </div>
                            <div className="p-3 bg-white/5 rounded-lg">
                                <span className="text-white/60">Eingereicht am:</span>
                                <span className="text-white ml-2">{format(new Date(), 'dd.MM.yyyy HH:mm', { locale: de })}</span>
                            </div>
                        </div>
                        <p className="text-white/60 text-sm text-center">
                            Bitte überprüfen Sie Ihre Angaben und klicken Sie auf "Absenden"
                        </p>
                    </div>
                );
            
            default:
                return null;
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex flex-col">
            <div className="flex-1 flex items-center justify-center p-4 py-8">
                <div className="w-full max-w-lg">
                    {/* Header */}
                    <div className="text-center mb-6">
                        <img src="/tgosm-logo.png" alt="TGOSM" className="w-16 h-16 mx-auto mb-3 object-contain" />
                        <h1 className="text-2xl font-bold text-white mb-1">Client Intake</h1>
                        <p className="text-white/60 text-sm">The Guardians of Social Media</p>
                    </div>

                    {/* Progress */}
                    <div className="flex gap-0.5 mb-4">
                        {Array.from({ length: totalSlides }).map((_, i) => (
                            <div
                                key={i}
                                className={`h-1 flex-1 rounded-full transition-all ${
                                    i <= currentSlide ? 'bg-primary' : 'bg-white/20'
                                }`}
                            />
                        ))}
                    </div>
                    <div className="text-center text-white/50 text-xs mb-3">
                        {currentSlide + 1} von {totalSlides}
                    </div>

                    {/* Form Card */}
                    <div className="bg-white/10 backdrop-blur-xl rounded-3xl p-5 border border-white/20 min-h-[380px] relative overflow-hidden">
                        <AnimatePresence mode="wait" custom={direction}>
                            <motion.div
                                key={currentSlide}
                                custom={direction}
                                variants={slideVariants}
                                initial="enter"
                                animate="center"
                                exit="exit"
                                transition={{ duration: 0.3 }}
                            >
                                {renderSlide()}
                            </motion.div>
                        </AnimatePresence>
                    </div>

                    {/* Navigation */}
                    <div className="flex gap-3 mt-5">
                        {currentSlide > 0 && (
                            <Button
                                variant="outline"
                                onClick={goPrev}
                                className="flex-1 bg-white/10 border-white/20 text-white hover:bg-white/20"
                            >
                                <ChevronLeft className="w-4 h-4 mr-1" /> Zurück
                            </Button>
                        )}
                        
                        {currentSlide < totalSlides - 1 ? (
                            <Button
                                onClick={goNext}
                                disabled={!canProceed()}
                                className="flex-1"
                                style={{ background: canProceed() ? 'linear-gradient(135deg, hsl(199, 100%, 50%) 0%, hsl(199, 100%, 40%) 100%)' : undefined }}
                            >
                                Weiter <ChevronRight className="w-4 h-4 ml-1" />
                            </Button>
                        ) : (
                            <Button
                                onClick={handleSubmit}
                                disabled={isSubmitting}
                                className="flex-1"
                                style={{ background: 'linear-gradient(135deg, hsl(199, 100%, 50%) 0%, hsl(199, 100%, 40%) 100%)' }}
                            >
                                {isSubmitting ? (
                                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Wird gesendet...</>
                                ) : (
                                    <>Absenden</>
                                )}
                            </Button>
                        )}
                    </div>
                </div>
            </div>
            
            {/* Footer */}
            <footer className="py-4 text-center text-white/40 text-xs">
                <p>© {new Date().getFullYear()} The Guardians of Social Media GmbH</p>
            </footer>
        </div>
    );
};

export default ClientIntakePage;
